import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Stack, Switch, Text, Title } from "@mantine/core";

import { Can } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";

const ORG_CAPABILITIES = [
  {
    key: "org_finalization_permitted",
    labelKey: "org_finalization_permitted",
    descriptionKey: "org_finalization_permitted_description",
  },
  {
    key: "direct_assignment_permitted",
    labelKey: "direct_assignment_permitted",
    descriptionKey: "direct_assignment_permitted_description",
  },
];

export default function OrganizationPermissions({
  organizationId,
  permissionKeys = [],
  onSuccess = () => {},
}) {
  const { t } = useTranslation();
  const [busyKey, setBusyKey] = useState(null);
  const [grantedKeys, setGrantedKeys] = useState(permissionKeys || []);

  useEffect(() => {
    setGrantedKeys(permissionKeys || []);
  }, [JSON.stringify(permissionKeys || [])]);

  const granted = new Set(grantedKeys);

  function togglePermission(permissionKey, currentlyGranted) {
    setBusyKey(permissionKey);
    const request = currentlyGranted
      ? axios.post(`/organizations/${organizationId}/remove-permission/`, {
          permission_key: permissionKey,
        })
      : axios.post(`/organizations/${organizationId}/permissions/`, {
          permission_key: permissionKey,
        });

    request
      .then(() => {
        setGrantedKeys((current) =>
          currentlyGranted
            ? current.filter((key) => key !== permissionKey)
            : [...current, permissionKey]
        );
        setBusyKey(null);
        toast.success(t("updated"));
        onSuccess();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  return (
    <Can permission={PERMISSIONS.ORGANIZATIONS_EDIT}>
      <Stack gap="md">
        <div>
          <Title order={5}>{t("organization_capabilities")}</Title>
          <Text size="sm" c="dimmed">
            {t("organization_capabilities_description")}
          </Text>
        </div>
        {ORG_CAPABILITIES.map((capability) => (
          <Switch
            key={capability.key}
            label={t(capability.labelKey)}
            description={t(capability.descriptionKey)}
            checked={granted.has(capability.key)}
            disabled={busyKey === capability.key}
            onChange={() =>
              togglePermission(capability.key, granted.has(capability.key))
            }
          />
        ))}
      </Stack>
    </Can>
  );
}
